import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import modelsService from "../../services/modelsService";
import { Save, CheckCircle, Settings } from "lucide-react";
import toast from "react-hot-toast";

const AgentSettingsTab = ({
  agentData,
  updateAgentData,
  saveAgent,
  isSavingAgent,
  saveAgentError,
  createdAgent,
  isEditMode,
}) => {
  const [selectedProvider, setSelectedProvider] = useState(
    agentData.model_provider || ""
  );

  const providersData = modelsService.getProviders();

  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Agent name is required")
      .min(3, "Name must be at least 3 characters")
      .max(50, "Name must not exceed 50 characters"),
    description: Yup.string()
      .required("Description is required")
      .min(10, "Description must be at least 10 characters")
      .max(200, "Description must not exceed 200 characters"),
    behavior_rules: Yup.string().required("Behavior rules are required"),
    escalation_conditions: Yup.string().required(
      "Escalation conditions are required"
    ),
    model_provider: Yup.string().required("Model provider is required"),
    model_name: Yup.string().required("Model selection is required"),
    hands_off_type: Yup.boolean(),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      updateAgentData(values);
      await saveAgent(values);

      if (isEditMode) {
        toast.success("Agent updated successfully!");
      } else {
        toast.success("Agent created successfully! You can now add knowledge.");
      }
    } catch (error) {
      console.error("Error saving agent:", error);
      toast.error(
        error.response?.data?.message ||
          `Failed to ${isEditMode ? "update" : "create"} agent`
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (createdAgent && !isEditMode) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="flex items-center justify-center w-10 h-10 bg-green-100 dark:bg-green-900/20 rounded-lg">
            <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Agent Created Successfully
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Your AI agent has been created and is ready for knowledge
              configuration.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Agent Name
              </label>
              <div className="text-lg font-semibold text-gray-900 dark:text-white">
                {createdAgent.name}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <div className="text-gray-700 dark:text-gray-300">
                {createdAgent.description}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Behavior Rules
              </label>
              <div className="text-gray-700 dark:text-gray-300 text-sm">
                {createdAgent.behavior_rules}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Model Provider
              </label>
              <div className="text-gray-900 dark:text-white font-medium">
                {providersData.providers.find(
                  (p) => p.id === createdAgent.model_provider
                )?.name || createdAgent.model_provider}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Model
              </label>
              <div className="text-gray-900 dark:text-white font-medium">
                {createdAgent.model_name}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Escalation Conditions
              </label>
              <div className="text-gray-700 dark:text-gray-300 text-sm">
                {createdAgent.escalation_conditions}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Hands-off Mode
              </label>
              <div className="flex items-center space-x-2">
                <div
                  className={`w-3 h-3 rounded-full ${
                    createdAgent.hands_off_type ? "bg-green-400" : "bg-gray-400"
                  }`}
                />
                <span className="text-gray-900 dark:text-white">
                  {createdAgent.hands_off_type ? "Enabled" : "Disabled"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-start space-x-3">
            <Settings className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100">
                Next Step: Add Knowledge
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                Navigate to the Knowledge tab to add training data for your
                agent. This will enable the Try AI functionality.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Agent Configuration
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Configure your AI agent's basic settings and model selection.
        </p>
      </div>

      <Formik
        initialValues={{
          name: agentData.name || "",
          description: agentData.description || "",
          behavior_rules: agentData.behavior_rules || "",
          escalation_conditions: agentData.escalation_conditions || "",
          model_provider: agentData.model_provider || "",
          model_name: agentData.model_name || "",
          hands_off_type: agentData.hands_off_type || false,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue, isSubmitting }) => {
          // Update selected provider when form value changes
          if (values.model_provider !== selectedProvider) {
            setSelectedProvider(values.model_provider);
          }

          return (
            <Form className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Agent Name */}
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Agent Name *
                  </label>
                  <Field
                    name="name"
                    type="text"
                    placeholder="e.g. Customer Support Bot"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  />
                  <ErrorMessage
                    name="name"
                    component="div"
                    className="mt-1 text-sm text-red-600 dark:text-red-400"
                  />
                </div>

                {/* Model Provider */}
                <div>
                  <label
                    htmlFor="model_provider"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Model Provider *
                  </label>
                  <Field
                    as="select"
                    name="model_provider"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select a provider</option>
                    {providersData.providers.map((provider) => (
                      <option key={provider.id} value={provider.id}>
                        {provider.name}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name="model_provider"
                    component="div"
                    className="mt-1 text-sm text-red-600 dark:text-red-400"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Description *
                </label>
                <Field
                  as="textarea"
                  name="description"
                  rows={3}
                  placeholder="Describe what your agent does and how it helps users..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
                <ErrorMessage
                  name="description"
                  component="div"
                  className="mt-1 text-sm text-red-600 dark:text-red-400"
                />
              </div>

              {/* Behavior Rules */}
              <div>
                <label
                  htmlFor="behavior_rules"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Behavior Rules *
                </label>
                <Field
                  as="textarea"
                  name="behavior_rules"
                  rows={3}
                  placeholder="e.g. Kamu adalah customer service yang ramah dan helpful. Gunakan bahasa Indonesia yang sopan."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
                <ErrorMessage
                  name="behavior_rules"
                  component="div"
                  className="mt-1 text-sm text-red-600 dark:text-red-400"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Define your agent's personality, tone, and behavioral
                  guidelines.
                </p>
              </div>

              {/* Escalation Conditions */}
              <div>
                <label
                  htmlFor="escalation_conditions"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Escalation Conditions *
                </label>
                <Field
                  as="textarea"
                  name="escalation_conditions"
                  rows={3}
                  placeholder="e.g. Escalate jika customer komplain atau butuh refund"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
                <ErrorMessage
                  name="escalation_conditions"
                  component="div"
                  className="mt-1 text-sm text-red-600 dark:text-red-400"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Specify when the agent should escalate conversations to human
                  support.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Model Selection */}
                <div>
                  <label
                    htmlFor="model_name"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Model *
                  </label>
                  <Field
                    name="model_name"
                    type="text"
                    placeholder={
                      selectedProvider
                        ? "e.g. gpt-4o, llama-3.3-70b-versatile"
                        : "Select provider first"
                    }
                    disabled={!selectedProvider}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-400"
                  />
                  <ErrorMessage
                    name="model_name"
                    component="div"
                    className="mt-1 text-sm text-red-600 dark:text-red-400"
                  />
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Enter the exact model name from your provider
                  </p>
                </div>

                {/* Hands-off Mode */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Hands-off Mode
                  </label>
                  <div className="flex items-center space-x-3">
                    <Field
                      type="checkbox"
                      name="hands_off_type"
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Enable automatic responses without human intervention
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="submit"
                  disabled={isSubmitting || isSavingAgent}
                  className="flex items-center space-x-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium transition-colors"
                >
                  <Save className="w-4 h-4" />
                  <span>
                    {isSubmitting || isSavingAgent
                      ? `${isEditMode ? "Updating" : "Creating"} Agent...`
                      : `${isEditMode ? "Update" : "Create"} Agent`}
                  </span>
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default AgentSettingsTab;
